import React, { CSSProperties } from 'react';
import { useDeviceDetect } from '../hooks/useDeviceDetect';

interface InactivityBarProps {
  inactivityProgress: number;
  isActive: boolean;
  isWarning: boolean;
  isInitialDelayComplete: boolean;
  isCountingDown?: boolean;
  inactivityTimeout: number;
  hideSeconds?: boolean;
}

const InactivityBar: React.FC<InactivityBarProps> = ({
  inactivityProgress,
  isActive,
  isWarning,
  isInitialDelayComplete,
  isCountingDown = true,
  inactivityTimeout,
  hideSeconds = false
}) => {
  // Use device detection for mobile-specific enhancements
  const { isMobile } = useDeviceDetect();
  
  // Don't show during initial delay
  if (!isInitialDelayComplete) {
    return null;
  }
  
  // Calculate remaining seconds using the actual timeout value
  const secondsRemaining = Math.max(0, Math.ceil(inactivityTimeout - (inactivityTimeout * inactivityProgress / 100)));
  
  // Determine the color based on progress percentage and activity state
  const getProgressColor = () => {
    // Always green when inactive or not counting down
    if (!isActive || !isCountingDown) return '#10B981'; // green-500
    
    // Use more incremental color stops for smoother transition when active
    if (inactivityProgress < 20) return '#10B981'; // green-500
    if (inactivityProgress < 40) return '#34D399'; // green-400
    if (inactivityProgress < 50) return '#FBBF24'; // yellow-400
    if (inactivityProgress < 60) return '#F59E0B'; // amber-500
    if (inactivityProgress < 70) return '#F97316'; // orange-500
    if (inactivityProgress < 80) return '#EF4444'; // red-500
    return '#DC2626'; // red-600
  };

  const barColor = isActive && isWarning ? '#DC2626' : getProgressColor();
  
  // Use CSS variables for dynamic styling
  const barStyle: CSSProperties = {
    width: !isActive ? '0%' : isCountingDown ? `${inactivityProgress}%` : '5%',
    backgroundColor: barColor,
    boxShadow: isActive && isWarning 
      ? '0 0 8px rgba(220, 38, 38, 0.6)' 
      : `0 0 4px ${barColor}`,
    transition: isActive && isCountingDown ? 'all 0.1s linear' : 'none',
  };

  // Warning animation class
  const animationClass = isActive && isWarning ? 'animate-pulse' : '';
  
  return (
    <>
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 h-1">
        <div 
          className={`h-full rounded-r inactivity-progress ${animationClass}`}
          style={barStyle}
        />
      </div>
      
      {/* Desktop Status Text Container - hide on mobile */}
      {!isMobile && (
        <div className={`text-center text-xs py-1 
          ${isActive && isWarning 
            ? 'bg-red-500/20 text-red-600 dark:text-red-400' 
            : 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'} 
          font-medium ${animationClass} inactivity-text`}
        >
          {isActive ? (
            isWarning ? (
              <div className="flex items-center justify-center gap-1">
                <span className="font-bold">WARNING!</span>
                <span>Keep typing to save your work!</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-1">
                <span className="font-semibold">Inactivity:</span>
                {isCountingDown ? (
                  hideSeconds ? (
                    <span className="font-mono text-orange-500">Active</span>
                  ) : (
                    <span className="font-mono">{secondsRemaining}s remaining</span>
                  )
                ) : (
                  <span className="font-mono text-green-500">Ready</span>
                )}
              </div>
            )
          ) : (
            <div className="flex items-center justify-center gap-1">
              <span className="font-semibold text-black dark:text-white">Start a session to activate timer</span>
            </div>
          )}
        </div>
      )}
      
      {/* Mobile warning message - separate from the component for better positioning */}
      {isMobile && isActive && isWarning && (
        <div className="mobile-inactivity-warning">
          {hideSeconds ? 
            "Keep typing to save your work!" : 
            `Keep typing to save your work! (${secondsRemaining}s)`
          }
        </div>
      )}
    </>
  );
};

export default InactivityBar; 